/**
 * ChartPanel — wrapper de UI sobre o MetricChart.
 *
 * Header com:
 *   - Seletor de métrica (temp/energy)
 *   - Toggle "mostrar porta" (overlay)
 *   - Botão remover painel
 *
 * Os dados são fornecidos pelo parent (ChartStack ou EquipmentDetail).
 */

import { useEffect, useRef, useState } from "react";
import { Trash2, Activity, DoorOpen } from "lucide-react";
import clsx from "clsx";

import { Card, CardBody } from "@/components/Card";
import { formatDuration } from "@/lib/format";
import { MetricChart, type ChartSeries, type DoorEvent } from "./MetricChart";

export type MetricKey = "temperature" | "energy" | "humidity" | "custom";

export const METRIC_OPTIONS: { value: MetricKey; label: string; unit: string }[] = [
  { value: "temperature", label: "Temperatura", unit: "°C" },
  { value: "energy",      label: "Energia",     unit: "W" },
  { value: "humidity",    label: "Umidade",     unit: "%" },
  { value: "custom",      label: "Personalizado", unit: "—" },
];

/**
 * Descrição de uma série DISPONÍVEL pra plotar — usado pelo modo "custom".
 * O parent (EquipmentDetail) constrói essa lista a partir de todas as
 * readings que o equipment tem, com cores/units já resolvidas.
 */
export interface AvailableSeries {
  /** ID estável (ex: "interna", "produto", "potencia"). */
  id: string;
  /** Rótulo curto pra mostrar no checkbox. */
  label: string;
  /** Unidade de medida — usada pra auto-agrupar em eixos Y. */
  unit: string;
  color: string;
  width?: number;
  values: (number | null)[];
}

/** Resumo agregado de aberturas de porta no período selecionado. */
export interface DoorStats {
  /** Quantas vezes a porta foi aberta. */
  count: number;
  /** Tempo total (segundos) que a porta ficou aberta. */
  totalSec: number;
}

interface Props {
  /** ID estável do painel (pra remover do layout). */
  panelId: string;
  /** Métrica atualmente selecionada. */
  metric: MetricKey;
  onMetricChange: (m: MetricKey) => void;
  /** Mostrar overlay de porta. */
  showDoor: boolean;
  onShowDoorChange: (v: boolean) => void;
  /** Permite remover este painel. */
  onRemove?: () => void;

  /** Dados pra plotar. */
  timestamps: number[];
  series: ChartSeries[];
  doorEvents?: DoorEvent[];
  /** Stats agregados de porta — exibidos no header quando showDoor=true. */
  doorStats?: DoorStats;
  loading?: boolean;
  syncKey?: string;

  /** Necessário quando metric="custom" — todas as séries disponíveis. */
  availableSeries?: AvailableSeries[];
  /** IDs das séries selecionadas no modo custom. */
  customSeriesIds?: string[];
  onCustomSeriesChange?: (ids: string[]) => void;
}

export function ChartPanel({
  panelId,
  metric,
  onMetricChange,
  showDoor,
  onShowDoorChange,
  onRemove,
  timestamps,
  series,
  doorEvents = [],
  doorStats,
  loading,
  syncKey,
  availableSeries,
  customSeriesIds,
  onCustomSeriesChange,
}: Props) {
  const opt = METRIC_OPTIONS.find((o) => o.value === metric)!;
  const hasDoorActivity =
    showDoor && doorStats && (doorStats.count > 0 || doorStats.totalSec > 0);

  // No modo custom, a unidade exibida é uma sumarização das selecionadas
  const customUnitLabel =
    metric === "custom" && customSeriesIds && availableSeries
      ? summarizeUnits(
          availableSeries.filter((s) => customSeriesIds.includes(s.id)),
        )
      : opt.unit;

  return (
    <Card className="overflow-hidden animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-100 dark:border-slate-800 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Activity className="h-4 w-4 text-brand-500 dark:text-brand-300" />
          <select
            value={metric}
            onChange={(e) => onMetricChange(e.target.value as MetricKey)}
            className={clsx(
              "rounded-md border px-2 py-1 text-sm font-medium",
              "bg-surface-50 border-surface-200 text-slate-700",
              "dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200",
              "focus:outline-none focus:ring-2 focus:ring-brand-400/40",
            )}
            aria-label="Selecionar métrica"
          >
            {METRIC_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="hidden sm:inline text-xs font-mono uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {customUnitLabel}
          </span>
          {metric === "custom" && availableSeries && onCustomSeriesChange && (
            <CustomSeriesPicker
              available={availableSeries}
              selected={customSeriesIds ?? []}
              onChange={onCustomSeriesChange}
            />
          )}
        </div>

        <div className="flex items-center gap-1">
          {hasDoorActivity && (
            <span
              className={clsx(
                "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full",
                "bg-amber-50 text-amber-700 text-[11px] font-medium tabular-nums",
                "dark:bg-amber-950/40 dark:text-amber-300",
              )}
              title={`${doorStats!.count} abertura(s) totalizando ${formatDuration(doorStats!.totalSec, true)} no período`}
            >
              <DoorOpen className="h-3 w-3" />
              {doorStats!.count}× · {formatDuration(doorStats!.totalSec)}
            </span>
          )}
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-slate-600 dark:text-slate-400 px-2 py-1 rounded hover:bg-surface-50 dark:hover:bg-slate-800">
            <input
              type="checkbox"
              checked={showDoor}
              onChange={(e) => onShowDoorChange(e.target.checked)}
              className="h-3.5 w-3.5 rounded accent-amber-500"
            />
            Porta
          </label>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
              aria-label="Remover painel"
              title="Remover painel"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <CardBody className="p-3">
        {loading ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">
            Carregando dados…
          </div>
        ) : timestamps.length === 0 || series.length === 0 ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-slate-400 dark:text-slate-500">
            {series.length === 0
              ? `Sem dados de ${opt.label.toLowerCase()} no período.`
              : "Sem dados no período selecionado."}
          </div>
        ) : (
          <MetricChart
            timestamps={timestamps}
            series={series}
            doorEvents={showDoor ? doorEvents : []}
            syncKey={syncKey ?? `panel-${panelId}`}
            height={280}
            yLabel={primaryUnit(series) ?? opt.unit}
            yLabel2={secondaryUnit(series)}
          />
        )}
      </CardBody>
    </Card>
  );
}

// ============================================================================
// CustomSeriesPicker — popover com checkboxes pra escolher quais séries
// plotar quando metric="custom".
// ============================================================================

function CustomSeriesPicker({
  available,
  selected,
  onChange,
}: {
  available: AvailableSeries[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Fecha quando clica fora
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
          "bg-surface-50 border-surface-200 text-slate-700",
          "dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200",
          "hover:bg-surface-100 dark:hover:bg-slate-700 transition-colors",
        )}
      >
        {selected.length} série{selected.length === 1 ? "" : "s"}
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 max-h-80 overflow-auto rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-card-hover">
          <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-surface-100 dark:border-slate-800">
            Séries disponíveis
          </div>
          {available.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-400 dark:text-slate-500">
              Sem séries com dados no período.
            </div>
          ) : (
            <ul className="py-1">
              {available.map((s) => (
                <li key={s.id}>
                  <label className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-surface-50 dark:hover:bg-slate-800/60 text-sm">
                    <input
                      type="checkbox"
                      checked={selected.includes(s.id)}
                      onChange={() => toggle(s.id)}
                      className="h-3.5 w-3.5 rounded"
                      style={{ accentColor: s.color }}
                    />
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-none"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="flex-1 text-slate-700 dark:text-slate-300">{s.label}</span>
                    <span className="text-xs font-mono text-slate-400 dark:text-slate-500">
                      {s.unit}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Helpers: agrupar séries por unidade de medida e descobrir labels dos eixos.
// ============================================================================

/** Lista única de units mantendo a ordem de aparição. */
function uniqueUnits(series: { unit?: string }[]): string[] {
  const out: string[] = [];
  for (const s of series) {
    const u = (s as any).unit ?? "";
    if (u && !out.includes(u)) out.push(u);
  }
  return out;
}

/** Resume múltiplas unidades de medida pra mostrar no header (ex: "°C + W"). */
function summarizeUnits(series: AvailableSeries[]): string {
  const units = uniqueUnits(series);
  if (units.length === 0) return "—";
  if (units.length === 1) return units[0];
  if (units.length === 2) return `${units[0]} + ${units[1]}`;
  return `${units[0]} + ${units.length - 1} mais`;
}

/** Label do eixo Y primário — primeira unidade que aparece nas séries. */
function primaryUnit(series: ChartSeries[]): string | undefined {
  for (const s of series) {
    if (s.axis !== 1 && (s as any).unit) return (s as any).unit;
  }
  return undefined;
}

/** Label do eixo Y secundário — primeira unidade no axis=1. */
function secondaryUnit(series: ChartSeries[]): string | undefined {
  for (const s of series) {
    if (s.axis === 1 && (s as any).unit) return (s as any).unit;
  }
  return undefined;
}
